/**
 * Child App Entry Point
 * Initializes the child-friendly interface with device-based authorization
 */

// Legacy/unsupported-device fallback (Requirement 7): tell index.html's
// head-level watchdog this module and its whole static import graph
// actually loaded and evaluated, so it never redirects to /legacy.html.
// Written as the first statement for readability, but ES module import
// hoisting means the static imports below still run first regardless of
// source order — which is actually the stronger signal here: this line only
// executes at all if every import below resolved and evaluated without
// throwing. If any of them fails to load or has a parse error, this line
// never runs, the flag never gets set, and the watchdog fires. The fallback
// page itself now lives entirely outside this app's own document (see
// legacy.html and index.html), so a supported device's document never
// contains that markup at all — there is nothing here to remove or hide,
// and nothing that can flash.
window.__kassieBootOk = true;

// ═══════════════════════════════════════════════════════════════
// STALE MODULE RECOVERY (must be first — catches import failures)
// After a deploy, the service worker may serve old chunk hashes.
// This detects the failure and auto-reloads once to get fresh assets.
// ═══════════════════════════════════════════════════════════════
(() => {
  const isStaleModuleError = (msg) =>
    msg?.includes('Failed to fetch dynamically imported module') ||
    msg?.includes('Importing a module script failed') ||
    msg?.includes('error loading dynamically imported module');

  const tryReload = () => {
    const lastReload = sessionStorage.getItem('kassie-sw-reload');
    const now = Date.now();
    // Only reload once per 10 seconds to avoid infinite loop
    if (!lastReload || (now - parseInt(lastReload)) > 10000) {
      console.warn('🔄 Stale module detected — reloading for fresh assets...');
      sessionStorage.setItem('kassie-sw-reload', now.toString());
      window.location.reload();
    }
  };

  window.addEventListener('error', (event) => {
    if (isStaleModuleError(event.message)) tryReload();
  });

  window.addEventListener('unhandledrejection', (event) => {
    if (isStaleModuleError(event.reason?.message)) tryReload();
  });
})();

// Import CSS
import './styles/fonts.css';
import './styles/main.css';
import './styles/recording-modals.css';

import { initDebugService } from './utils/debugService.js';
import { initializeChildApp } from './app-new.js';
import { initializeOfflineStorage } from './services/offline.js';
import { initI18n, t } from './i18n/config.js';
import { initOrientationEnforcement } from './utils/orientationLock.js';
import { registerGlobalAudioUnlock } from './utils/audioUnlock.js';
import { probeCapabilities } from './utils/capabilities.js';
import './preview-handler.js'; // Import preview handler for preview mode support
import './utils/screenDebugger.js'; // Import screen debugger for development
import './utils/deviceDetection.js'; // Import device detection for responsive design (auto-initializes)
import './utils/pwaInstall.js'; // Register beforeinstallprompt listener early

/**
 * Early loader init is handled inside init() — no separate call needed.
 * Previously showInitialKassieLoader() had a race condition where its
 * async import resolved AFTER init() had already hidden the loader,
 * causing the loader to re-appear permanently.
 */

/**
 * Check for app updates using build version
 * Uses semantic versioning to determine if caches should be cleared
 * Only clears caches on major version changes (e.g., 1.x.x → 2.x.x)
 */
async function checkForUpdates() {
  try {
    // Only check in production
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      return;
    }

    // Get version from build-time environment variable
    const buildVersion = import.meta.env.VITE_APP_VERSION || 'dev';
    const storedVersion = localStorage.getItem('app_version');

    if (storedVersion && storedVersion !== buildVersion) {
      console.log(`🔄 New version detected: ${buildVersion} (was: ${storedVersion})`);

      // Parse semantic versions (MAJOR.MINOR.PATCH)
      const parseBuildVersion = (version) => {
        const match = version.match(/^(\d+)\.(\d+)\.(\d+)/);
        if (!match) return null;
        return {
          major: parseInt(match[1], 10),
          minor: parseInt(match[2], 10),
          patch: parseInt(match[3], 10),
        };
      };

      const oldVer = parseBuildVersion(storedVersion);
      const newVer = parseBuildVersion(buildVersion);

      // Clear caches if major OR minor version changed (more aggressive for frequent updates)
      if (oldVer && newVer && (oldVer.major !== newVer.major || oldVer.minor !== newVer.minor)) {
        console.log(
          `🗑️ Version change (${oldVer.major}.${oldVer.minor} → ${newVer.major}.${newVer.minor}), clearing caches`
        );

        // Clear service worker caches
        if ('caches' in window) {
          const cacheNames = await caches.keys();
          await Promise.all(cacheNames.map((name) => caches.delete(name)));
        }

        // Clear localStorage caches (but keep version and device auth)
        // These are the REAL key names. Clearing them de-authorizes the device
        // and forces a full re-enrolment — never do that on a routine update.
        const keysToKeep = [
          'app_version',
          'deviceAuthorization',
          'authorizedFamily',
          'kassie_device_id',
        ];
        const allKeys = Object.keys(localStorage);
        allKeys.forEach((key) => {
          if (!keysToKeep.includes(key)) {
            localStorage.removeItem(key);
          }
        });

        console.log(`✅ Caches cleared for major version update`);
      } else {
        console.log(`✨ Minor/patch update, keeping caches`);
      }

      // Store new version
      localStorage.setItem('app_version', buildVersion);

      // Force reload to get new version
      window.location.reload(true);
    } else if (!storedVersion) {
      // Store version on first load
      localStorage.setItem('app_version', buildVersion);
      console.log(`📦 App version: ${buildVersion}`);
    }
  } catch (error) {
    console.warn('Could not check for updates:', error);
  }
}

/**
 * Initialize child app on page load
 */
async function init() {
  try {
    // Initialize remote debug service (no-op if magic param absent)
    initDebugService();

    console.log('🚀 Starting Kassie Child App...');

    // Register global audio unlock for iOS Safari/PWA support.
    // Attaches listeners on first user gesture so all subsequent Audio.play() calls work.
    registerGlobalAudioUnlock();

    // One-shot platform capability probe (dvh support, IndexedDB health), for
    // diagnosing future device-specific reports from a single console line.
    // Deliberately not awaited — must never delay the loader below (Req 4.5).
    probeCapabilities();

    // Show loader immediately (V3 — character engine with layer toggling)
    const { initKassieLoaderV3 } = await import('./utils/kassieLoaderV3.js');
    await initKassieLoaderV3();
    window.showLoader();

    // Dev/test-only: ?mockload=<ms> artificially stalls boot so the loader's
    // hide/retarget behaviour (see kassieLoaderV3.js's hideLoader()) can
    // actually be watched — on localhost the real boot sequence below
    // usually finishes in well under 300ms, too fast to see the loader sit
    // still before it starts hiding. Capped at 15s and ignored outside
    // localhost so it can never affect a real deployed load.
    const isLocalhost =
      window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    if (isLocalhost) {
      const mockLoadMs = parseInt(new URLSearchParams(window.location.search).get('mockload'), 10);
      if (Number.isFinite(mockLoadMs) && mockLoadMs > 0) {
        await new Promise((resolve) => setTimeout(resolve, Math.min(mockLoadMs, 15000)));
      }
    }

    // Check for updates first
    await checkForUpdates();

    // Initialize i18n first
    await initI18n();

    // Initialize offline storage
    await initializeOfflineStorage();

    // Resume any recordings that never reached the server (#150).
    // A closed tab cannot upload, so this is what makes the guarantee true:
    // nothing is lost, and it uploads the moment the app is open again.
    const { startRecordingFlushWatchers } = await import('./services/recordingStore.js');
    startRecordingFlushWatchers();

    // Initialize orientation enforcement (app-wide)
    // Shows warning overlay for phones/small tablets in landscape
    // Tablets ≥1024px can use landscape freely (double-page mode in reader)
    await initOrientationEnforcement({
      useLock: false, // Don't use API lock (doesn't work in browsers)
      useCSS: false, // Don't use CSS rotation (can cause issues)
      useWarning: true, // Show gentle warning message for phones in landscape
    });

    // Initialize child app
    // Initialize recording diagnostics overlay BEFORE app init
    // so boot-time diagnostic entries are captured and visible
    const { isFeatureEnabled } = await import('./config/featureFlags.js');
    if (isFeatureEnabled('recordingDebug')) {
      const { initRecordingDebug } = await import('./utils/recordingDebug.js');
      initRecordingDebug();
    }

    await initializeChildApp();

    // Hide the Kassie loader now that the app is ready
    window.hideLoader();

    console.log('✅ Kassie Child App initialized successfully');

    // Register PWA update banner (shows when new version is available)
    const { registerUpdateBanner } = await import('./utils/pwaUpdateBanner.js');
    registerUpdateBanner();
  } catch (error) {
    console.error('❌ Failed to initialize Kassie Child App:', error);

    // Always hide loader on error
    window.hideLoader();

    // Show error screen
    showInitializationError(error);
  }
}

/**
 * Show initialization error
 */
function showInitializationError(error) {
  const app = document.getElementById('app');

  app.innerHTML = `
    <div class="init-error-screen">
      <img src="/svg/branding/KassieAvatar.svg" alt="Kassie" width="120">
      <h1>${t('child.errors.oops')}</h1>
      <p>${t('child.errors.couldNotLoadData')} ${t('child.errors.tryAgainOrGoBack')}</p>

      <div class="error-actions">
        <button onclick="location.reload()" class="custom-button">
          <div class="bottom-div greenBG"></div>
          <div class="top-div kassieBG">
            <img class="icon-placeholder" src="/svg/icons/Reload.svg" alt="" width="20" height="20" />
            <span class="button-text">${t('child.actions.tryAgain')}</span>
          </div>
        </button>

        <button onclick="window.history.back()" class="custom-button">
          <div class="bottom-div cyanBG"></div>
          <div class="top-div kassieBG">
            <img class="icon-placeholder" src="/svg/icons/LeftArrow.svg" alt="" width="20" height="20" />
            <span class="button-text">${t('child.actions.goBack')}</span>
          </div>
        </button>
      </div>

      ${process.env.NODE_ENV === 'development'
      ? `
        <details class="error-details">
          <summary>Fout besonderhede (vir ontwikkelaars)</summary>
          <pre>${error.message}\n${error.stack}</pre>
        </details>
      `
      : ''
    }
    </div>
  `;

  // Styles are in base.css — no inline styles needed
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

// Some cases the page loads longer than 15s, Schalk disabled this
// until we optimise everything properly
// window.addEventListener('load', () => {
//   setTimeout(() => {
//     if (window.hideLoader) window.hideLoader();
//   }, 15000);
// });

/**
 * Debug keyboard shortcuts (development only)
 * Delete: Clear ALL localStorage (simulates brand new device)
 * Shift+Delete: Clear current profile only (simulates new user on existing device)
 */
document.addEventListener('keydown', (e) => {
  if (e.key !== 'Delete') return;

  if (e.shiftKey) {
    // Shift+Delete: Remove current profile data only
    // Simulates: existing device, but child needs to re-select profile
    localStorage.removeItem('selectedProfile');
    // Clear offline cached content for the profile
    const keys = Object.keys(localStorage);
    keys.forEach((key) => {
      if (key.startsWith('kassie_module_')) {
        localStorage.removeItem(key);
      }
    });
    console.log('🧹 [DEBUG] Cleared current profile (Shift+Delete)');
    console.log('   Simulates: child needs to pick a profile on authorized device');
    window.location.reload();
  } else {
    // Delete: Clear ALL localStorage
    // Simulates: brand new device, never activated
    localStorage.clear();
    console.log('🧹 [DEBUG] Cleared ALL localStorage (Delete)');
    console.log('   Simulates: brand new device, needs activation code');
    window.location.reload();
  }
});

/**
 * Debug: Press 'Q' to toggle the device activation screen overlay
 * Useful for testing the activation code entry UI without clearing localStorage
 */
document.addEventListener('keydown', async (e) => {
  if (e.key !== 'q' && e.key !== 'Q') return;

  const app = document.getElementById('app');
  const existingActivation = app.querySelector('.device-activation-container');

  if (existingActivation) {
    // Activation screen is showing — remove it and reload app
    console.log('🧹 [DEBUG] Hiding activation screen (Q)');
    window.location.reload();
  } else {
    // Show activation screen overlay
    console.log('🧹 [DEBUG] Showing activation screen (Q)');
    const { renderDeviceActivation } = await import('./views/DeviceActivation.js');
    await renderDeviceActivation();
  }
});
